import {
  ExternalMessageFragment,
  ExternalMessageType,
  GetConversationExternalMessagesQuery,
} from '@shared/generated/graphql';
import {
  ConversationContributionRole,
  ConversationMessageEvent,
  ConversationMessageEventType,
  MessageAttachment,
  MessageSender,
} from '@shared/types/conversation';
import DOMPurify from 'dompurify';
import { ExternalPlatform } from '../../types/conversation';

const toMessageSender = (
  fragment: ExternalMessageFragment
): MessageSender | undefined => {
  if (!fragment.user) return undefined;
  return {
    id: fragment.user.id,
    name: fragment.user.profile?.name ?? undefined,
    username: fragment.user.username ?? undefined,
    firstName: fragment.user.firstName ?? undefined,
    lastName: fragment.user.lastName ?? undefined,
  };
};

const toMessageAttachments = (
  fragment: ExternalMessageFragment
): MessageAttachment[] | undefined =>
  fragment.documents?.map((d) => ({
    id: d.id,
    name: d.name ?? undefined,
    url: d.url,
    metadata: d.metadata as Record<string, any>,
  }));

const toSanitizedEmailHtmlBody = (
  fragment: ExternalMessageFragment
): string => {
  const rawBody =
    fragment.htmlBody ??
    (typeof fragment.content === 'string'
      ? fragment.content
      : fragment.content?.transcript);
  return rawBody ? DOMPurify.sanitize(rawBody) : '';
};

export const conversationMessageEventFromExternalMessageFragment = (
  fragment: ExternalMessageFragment
): ConversationMessageEvent => {
  switch (fragment.type) {
    case ExternalMessageType.VOICE_TRANSCRIPT:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.VOICE_CONTRIBUTION,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        transcript: fragment.content?.transcript ?? '',
        conversationId: fragment.conversationId ?? undefined,
      };
    case ExternalMessageType.GMAIL_MESSAGE:
    case ExternalMessageType.MICROSOFT_MESSAGE:
    case ExternalMessageType.VMAIL_MESSAGE:
    case ExternalMessageType.AGENTMAIL_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.EMAIL_CONTRIBUTION,
        createdAt: new Date(fragment.createdAt),
        sanitizedHtmlBody: toSanitizedEmailHtmlBody(fragment),
        subject: fragment.emailMetadata?.subject ?? '',
        sender: fragment.emailMetadata?.sender ?? '',
        toRecipients: fragment.emailMetadata?.toRecipients ?? [],
        emailReceivedAt: fragment.emailMetadata?.receivedDateTime
          ? new Date(fragment.emailMetadata.receivedDateTime)
          : undefined,
        role: fragment.emailMetadata?.role ?? undefined,
        conversationId: fragment.conversationId ?? undefined,
      };
    case ExternalMessageType.SYSTEM_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.SYSTEM_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        message: fragment.content ?? '',
        conversationId: fragment.conversationId ?? undefined,
      };
    case ExternalMessageType.CHAT_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        message: fragment.content?.transcript ?? '',
        conversationId: fragment.conversationId ?? undefined,
        sender: toMessageSender(fragment),
        card: fragment.content?.card ?? undefined,
        attachments: toMessageAttachments(fragment),
      };
    case ExternalMessageType.MICROSOFT_TEAMS_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        message: fragment.content?.transcript ?? '',
        externalPlatform: ExternalPlatform.MICROSOFT_TEAMS,
        conversationId: fragment.conversationId ?? undefined,
        canonicalMessageId: fragment.canonicalMessageId ?? undefined,
        sender: toMessageSender(fragment),
      };
    case ExternalMessageType.SLACK_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        message: fragment.content?.transcript ?? '',
        externalPlatform: ExternalPlatform.SLACK,
        conversationId: fragment.conversationId ?? undefined,
        canonicalMessageId: fragment.canonicalMessageId ?? undefined,
        sender: toMessageSender(fragment),
      };
    case ExternalMessageType.PYLON_MESSAGE:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        message: fragment.content?.transcript ?? '',
        conversationId: fragment.conversationId ?? undefined,
      };
    case ExternalMessageType.ASSISTANT_EMAIL:
      if (fragment.htmlBody || fragment.emailMetadata?.subject) {
        return {
          id: fragment.id,
          type: ConversationMessageEventType.EMAIL_CONTRIBUTION,
          createdAt: new Date(fragment.createdAt),
          sanitizedHtmlBody: toSanitizedEmailHtmlBody(fragment),
          subject: fragment.emailMetadata?.subject ?? '',
          sender: fragment.emailMetadata?.sender ?? '',
          toRecipients: fragment.emailMetadata?.toRecipients ?? [],
          emailReceivedAt: fragment.emailMetadata?.receivedDateTime
            ? new Date(fragment.emailMetadata.receivedDateTime)
            : undefined,
          role: fragment.emailMetadata?.role ?? undefined,
          conversationId: fragment.conversationId ?? undefined,
        };
      }
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.emailMetadata?.role ?? ConversationContributionRole.USER,
        message: fragment.content ?? '',
        externalPlatform: ExternalPlatform.ASSISTANT_EMAIL,
        conversationId: fragment.conversationId ?? undefined,
        canonicalMessageId: fragment.canonicalMessageId ?? undefined,
      };
    case ExternalMessageType.TWILIO_SMS:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.CHAT_MESSAGE,
        createdAt: new Date(fragment.createdAt),
        role: fragment.content?.role ?? ConversationContributionRole.USER,
        message: fragment.content?.body ?? '',
        externalPlatform: ExternalPlatform.SMS,
        conversationId: fragment.conversationId ?? undefined,
        attachments: toMessageAttachments(fragment),
      };
    default:
      return {
        id: fragment.id,
        type: ConversationMessageEventType.UNKNOWN,
        createdAt: new Date(fragment.createdAt),
        conversationId: fragment.conversationId ?? undefined,
      };
  }
};

export const toConversationMessageEvents = (
  query?: GetConversationExternalMessagesQuery
): ConversationMessageEvent[] => {
  return (
    query?.conversationExternalMessages?.edges.map((e) =>
      conversationMessageEventFromExternalMessageFragment(e.node)
    ) ?? []
  );
};
